/**
 * 📊 useGainsRibbon — Hook de configuração do Ribbon para o Monitor de Ganhos
 *
 * Retorna RibbonConfig com grupos: Navegação, Arquivo, Exportar, Edição, Análise, Ferramentas
 */

import { useMemo } from 'react';
import {
  Home, ChevronLeft, Save, FileSpreadsheet, FileText, FileDown, Image as ImageIcon,
  Plus,
  Filter, RefreshCw, BarChart3, TrendingUp, DollarSign, AlertCircle, Settings,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';

const NOOP = () => {};

export interface GainsRibbonDeps {
  // Navegação
  onGoHome?: () => void;
  onBackToProject?: () => void;
  // Arquivo
  onSave?: () => void;
  onImportExcel?: () => void;
  onDownloadTemplate?: () => void;
  // Exportar
  onExportExcel?: () => void;
  onExportPDF?: () => void;
  onExportImage?: () => void;
  // Edição
  onNewKaizen?: () => void;
  // Análise
  onFilter?: () => void;
  hasActiveFilters?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onDashboard?: () => void;
  onTrends?: () => void;
  onROI?: () => void;
  onAlerts?: () => void;
  alertCount?: number;
  onSettings?: () => void;
  // Stats slot
  statsWidget?: React.ReactNode;
}

export function useGainsRibbon(deps: GainsRibbonDeps): RibbonConfig {
  return useMemo<RibbonConfig>(() => ({
    moduleId: 'gains',
    groups: [
      // ─── Navegação (LEFT) ───────────────────────
      {
        id: 'navegacao',
        label: 'Navegação',
        position: 'left',
        hidden: !deps.onGoHome && !deps.onBackToProject,
        actions: [
          { id: 'home', label: 'Início', icon: Home, onClick: deps.onGoHome ?? NOOP, hidden: !deps.onGoHome },
          { id: 'back', label: 'Projeto', icon: ChevronLeft, onClick: deps.onBackToProject ?? NOOP, hidden: !deps.onBackToProject },
        ],
      },
      // ─── Arquivo (LEFT) ─────────────────────────
      {
        id: 'arquivo',
        label: 'Arquivo',
        position: 'left',
        actions: [
          { id: 'save', label: 'Salvar', icon: Save, onClick: deps.onSave ?? NOOP, variant: 'primary', disabled: !deps.onSave, hidden: !deps.onSave },
          { id: 'new-kaizen', label: 'Novo Kaizen', icon: Plus, onClick: deps.onNewKaizen ?? NOOP, variant: 'success' },
          {
            id: 'import',
            label: 'Importar',
            icon: FileSpreadsheet,
            onClick: deps.onImportExcel ?? NOOP,
            dropdown: [
              { id: 'import-excel', label: 'Importar Kaizens do Excel', icon: FileSpreadsheet, onClick: deps.onImportExcel ?? NOOP },
              { id: 'download-tpl', label: 'Baixar Template Excel', icon: FileDown, onClick: deps.onDownloadTemplate ?? NOOP },
            ],
          },
        ],
      },
      // ─── Exportar (LEFT) ────────────────────────
      {
        id: 'exportar',
        label: 'Exportar',
        position: 'left',
        actions: [
          { id: 'export-excel', label: 'Excel', icon: FileSpreadsheet, onClick: deps.onExportExcel ?? NOOP },
          { id: 'export-pdf', label: 'PDF', icon: FileText, onClick: deps.onExportPDF ?? NOOP },
          { id: 'export-image', label: 'Imagem', icon: ImageIcon, onClick: deps.onExportImage ?? NOOP },
        ],
      },
      // ─── Análise (CENTER) ───────────────────────
      {
        id: 'analise',
        label: 'Análise',
        position: 'center',
        actions: [
          { id: 'dashboard', label: 'Dashboard', icon: BarChart3, onClick: deps.onDashboard ?? NOOP, tooltip: 'Dashboard estratégico: ROI, Sustentação' },
          { id: 'trends', label: 'Tendências', icon: TrendingUp, onClick: deps.onTrends ?? NOOP },
          { id: 'roi', label: 'ROI', icon: DollarSign, onClick: deps.onROI ?? NOOP, tooltip: 'Análise de Retorno sobre Investimento' },
          {
            id: 'alerts',
            label: 'Alertas',
            icon: AlertCircle,
            onClick: deps.onAlerts ?? NOOP,
            badge: deps.alertCount && deps.alertCount > 0 ? deps.alertCount : undefined,
            variant: (deps.alertCount ?? 0) > 0 ? 'danger' : 'default',
          },
        ],
      },
      // ─── Filtros (RIGHT) ────────────────────────
      {
        id: 'filtros',
        label: 'Filtros',
        position: 'right',
        actions: [
          { id: 'filter', label: 'Filtrar', icon: Filter, onClick: deps.onFilter ?? NOOP, active: deps.hasActiveFilters },
          { id: 'refresh', label: 'Atualizar', icon: RefreshCw, onClick: deps.onRefresh ?? NOOP, disabled: deps.isRefreshing },
        ],
      },
      // ─── Ferramentas (RIGHT) ────────────────────
      {
        id: 'ferramentas',
        label: 'Ferramentas',
        position: 'right',
        actions: [
          { id: 'settings', label: 'Configurar', icon: Settings, onClick: deps.onSettings ?? NOOP, hidden: !deps.onSettings },
        ],
      },
    ],
    slots: {
      afterGroups: deps.statsWidget,
    },
  }), [deps]);
}
